import { useEffect, useState } from 'react';
import { useRouter } from 'next/router';
import { isError } from '@belong/api';
import { getConfig } from '@belong/configs/next/config';
import {
  errorCodes,
  findAuthenticator,
  getElevatedAuthToken,
  getMfaEnrolmentToken,
  IAuth0ErrorResponse,
  MFA_ROUTES,
  useAuthContext
} from '@belong/domain-authentication';
import { decodeJwtToken } from '@belong/jwt';
import { logger } from '@belong/logging/logger';
import { useAnalyticsEvents } from '@belong/providers/analyticsEvents';
import {
  IAuthenticators,
  IErrorResponse,
  ILoginRequest,
  ILoginResponse,
  IRouteDefinition,
  IUserIdToken
} from '@belong/types';
import { getUrlPath, matchRoute, pushRoute, withoutBasePath } from '@belong/url-utils';
import { getCorrelationId, isUserMfaEnrolled } from '@belong/user';
import { LoginHeadingVariant, TLoginHeadingVariant } from '../components/FormLogin/types';

interface IViewModel {
  isRedirectingToMfa: boolean;
  destinationOverrideUrl?: string;
  initialAlertReason?: string;
  handleLoginSubmit: (values: ILoginRequest) => Promise<ILoginResponse | IErrorResponse>;
  headingVariant: TLoginHeadingVariant;
}

const getQueryString = (param: string[] | string | undefined): string | undefined => {
  return Array.isArray(param) ? param[0] : param;
};

export type IUserLogin = (
  payload: ILoginRequest,
  correlationId?: string,
  elevatePermissions?: boolean,
  enrolmentTokenRequired?: boolean
) => Promise<ILoginResponse | IErrorResponse>;

export type IUpdateUserDetails = (loginRequest: ILoginRequest) => void;

const { basePath } = getConfig().publicRuntimeConfig;

export const useViewModel = (
  userLogin: IUserLogin,
  updateUserDetails: IUpdateUserDetails,
  internalRoutesPattern: IRouteDefinition[]
): IViewModel => {
  const { login: authLogin, redirectOnContext, setMfaToken, getAuthenticators } = useAuthContext();
  const { query } = useRouter();
  const analyticsEvents = useAnalyticsEvents();
  const [isRedirectingToMfa, setIsRedirectingToMfa] = useState<boolean>(false);

  /**
   * We can show an alert when the users' session has expired.
   * The query will be something like `?reason=session_expired`
   */
  const initialAlertReason = getQueryString(query.reason);

  /**
   * It's possible the user has tried to bypass MFA, if we detect
   * this, we bounce them back here as MFA is considered part of "logging in".
   * In any other use case the user should be in a logged out state.
   */
  useEffect(() => {
    //  TODO: use constants for the mfa-incomplete later once we decide where should we put MFA_INCOMPLETE
    if (initialAlertReason === 'mfa-incomplete') {
      redirectToMfa().catch(logger.error);
    }
  }, [initialAlertReason]);

  /**
   * On successful login, we can send the user to somewhere other than
   * the default by passing a `?redirect=` parameter in the URL.
   */
  const destinationOverrideUrl = getQueryString(query.redirect);
  const redirectUrl = withoutBasePath(destinationOverrideUrl, basePath);

  let redirectQuery = {};
  let headingVariant: TLoginHeadingVariant = LoginHeadingVariant.DEFAULT;

  if (redirectUrl) {
    redirectQuery = {
      query: {
        redirect: redirectUrl
      }
    };

    /**
     * Attempt to match the redirect path to a custom heading.
     * If path is compiled (contains actual values rather than :params) then we can match the pattern and extract the pathname.
     * If no redirect or no match just use default heading
     */
    const redirectPath = matchRoute(getUrlPath(redirectUrl), internalRoutesPattern)?.pathname ?? redirectUrl;
    if (LoginHeadingVariant[redirectPath]) {
      headingVariant = LoginHeadingVariant[redirectPath];
    }
  }

  const redirectToMfa = async (
    authenticatorsOverride?: IAuthenticators | IAuth0ErrorResponse
  ): Promise<void | IAuth0ErrorResponse> => {
    setIsRedirectingToMfa(true);

    const authenticatorsResponse = authenticatorsOverride ?? (await getAuthenticators());
    if (isError(authenticatorsResponse)) {
      setIsRedirectingToMfa(false);
      return authenticatorsResponse;
    }

    /*
     * go into MFA enrolment flow
     */
    if (!isUserMfaEnrolled(authenticatorsResponse)) {
      logger.debug(`Login successful, redirecting the user to ${MFA_ROUTES.MFA}`);
      await pushRoute({
        pathname: MFA_ROUTES.MFA,
        ...redirectQuery
      });

      return;
    }

    /*
     * go into MFA step up flow
     */
    if (isUserMfaEnrolled(authenticatorsResponse)) {
      logger.debug(`Login successful, redirecting the user to ${MFA_ROUTES.MFA_STEP_UP}`);
      /**
       * We need to use `findAuthenticator` with the response, rather than using the `hasAuthenticator` because
       * the update state from getAuthenticators has not yet completed so it seems as though there is no
       * authenticators
       */
      const hasSmsAuthenticator = !!findAuthenticator(authenticatorsResponse, 'sms');

      if (hasSmsAuthenticator) {
        logger.debug(`Login successful, redirecting the user to ${MFA_ROUTES.MFA_STEP_UP}`);
        await pushRoute({
          pathname: MFA_ROUTES.MFA_STEP_UP,
          ...redirectQuery
        });
      } else {
        logger.debug(`Login successful, redirecting the user to ${MFA_ROUTES.MFA_STEP_UP_EMAIL}`);
        await pushRoute({
          pathname: MFA_ROUTES.MFA_STEP_UP_EMAIL,
          ...redirectQuery
        });
      }

      return;
    }

    redirectOnContext(withoutBasePath(destinationOverrideUrl, basePath));
    setIsRedirectingToMfa(false);
  };

  /**
   * MFA bypass, currently used for local dev. Exchanges the enrolment token for
   * an elevated auth token, logs the user in and redirects.
   */
  const handleMfaBypass = async (
    loginValues: ILoginRequest,
    enrolmentTokenResponse: ILoginResponse
  ): Promise<ILoginResponse | IErrorResponse> => {
    const elevatedAuthTokenResponse = await getElevatedAuthToken({
      ...loginValues,
      accessToken: enrolmentTokenResponse.accessToken
    });

    if (isError(elevatedAuthTokenResponse)) {
      return elevatedAuthTokenResponse;
    }

    authLogin(elevatedAuthTokenResponse, enrolmentTokenResponse);
    redirectOnContext(withoutBasePath(destinationOverrideUrl, basePath));
    return enrolmentTokenResponse;
  };

  /**
   * Applies the appropriate login side effects before the MFA redirect.
   * Returns an error response to bail out with, or `null` to continue.
   */
  const finaliseMfaEnrolment = (
    loginValues: ILoginRequest,
    isUserEnrolled: boolean,
    authTokenResponse: ILoginResponse,
    enrolmentTokenResponse: ILoginResponse | IErrorResponse
  ): IErrorResponse | null => {
    if (!isUserEnrolled) {
      if (isError(enrolmentTokenResponse)) {
        return enrolmentTokenResponse;
      }

      authLogin(authTokenResponse, enrolmentTokenResponse);
      return null;
    }

    // Save user creds to User store temporarily.
    // We'll use these at the end of MFA Step Up to retrieve an elevated access token
    updateUserDetails(loginValues);

    if (isError(enrolmentTokenResponse)) {
      setMfaToken(enrolmentTokenResponse.mfaToken);
    }

    return null;
  };

  const handleLoginSubmit = async (values: ILoginRequest): Promise<ILoginResponse | IErrorResponse> => {
    const nonElevatedAuthTokenResponse = await userLogin(values);
    const didFail = isError(nonElevatedAuthTokenResponse);

    if (didFail) {
      analyticsEvents.send({
        action: 'server error',
        category: 'form',
        error: nonElevatedAuthTokenResponse
      });

      logger.info(`${getCorrelationId()} Login failed with: ${nonElevatedAuthTokenResponse?.errorDescription}`);

      return nonElevatedAuthTokenResponse;
    }

    /**
     * This is usually globally set in <App> -> <AnalyticsContext> but we need
     * to manually set it here because the other one reads from the store and
     * at this point we haven't updated the store yet.
     */
    const auth0Id =
      nonElevatedAuthTokenResponse?.idToken && decodeJwtToken<IUserIdToken>(nonElevatedAuthTokenResponse.idToken)?.sub;

    const analyticsOverride = { auth0Id, isLoggedIn: true };
    analyticsEvents.addContext(analyticsOverride);
    analyticsEvents.send({
      category: 'api response',
      action: 'success',
      label: 'login success'
    });

    analyticsEvents.removeContext(analyticsOverride);

    // Record log to associate correlationId to a user
    logger.info(`${getCorrelationId()} Login successful ${auth0Id?.split('|').pop()}`);

    const mfaEnrolmentTokenResponse = await getMfaEnrolmentToken(values);

    const requiresElevation =
      isError(mfaEnrolmentTokenResponse) && mfaEnrolmentTokenResponse.errorCode === errorCodes.mfaRequired;

    if (isError(mfaEnrolmentTokenResponse) && !requiresElevation) {
      return mfaEnrolmentTokenResponse;
    }

    const bearerToken =
      isError(mfaEnrolmentTokenResponse) && requiresElevation
        ? mfaEnrolmentTokenResponse.mfaToken
        : mfaEnrolmentTokenResponse.accessToken;

    const authenticatorsResponse = await getAuthenticators(true, bearerToken);

    if (isError(authenticatorsResponse)) {
      logger.info(`${getCorrelationId()} MFA failed with ${authenticatorsResponse?.errorDescription}`);
      return authenticatorsResponse;
    }

    const isUserEnrolled = isUserMfaEnrolled(authenticatorsResponse);

    if (!requiresElevation && isUserEnrolled) {
      return handleMfaBypass(values, mfaEnrolmentTokenResponse as ILoginResponse);
    }

    const enrolmentResult = finaliseMfaEnrolment(
      values,
      isUserEnrolled,
      nonElevatedAuthTokenResponse,
      mfaEnrolmentTokenResponse
    );
    if (enrolmentResult) {
      return enrolmentResult;
    }

    const mfaRedirectResponse = await redirectToMfa(authenticatorsResponse);
    if (isError(mfaRedirectResponse)) {
      return mfaRedirectResponse;
    }

    return nonElevatedAuthTokenResponse;
  };

  return {
    isRedirectingToMfa,
    destinationOverrideUrl,
    initialAlertReason,
    handleLoginSubmit,
    headingVariant
  };
};
